import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageHeader from "../components/PageHeader";
import TableToolbar from "../components/TableToolbar";
import DataTable from "../components/DataTable";
import Toast, { useToast } from "../components/Toast";
import {
  obtenerEmpresas,
  crearEmpresa,
  actualizarEmpresa,
} from "../services/empresaService";

const EMPTY_FORM = {
  EMPRESA: "",
  NOMBRE_EMPRESA: "",
  CALLE_EMPRESA: "",
  COLONIA_EMPRESA: "",
  NUM_EXT_EMPRESA: "",
  NUM_INT_EMPRESA: "",
  CP_EMPRESA: "",
  TELEFONOS_EMPRESA: "",
  CIUDAD_EMPRESA: "",
  ESTADO_EMPRESA: "",
  RFC_EMPRESA: "",
  PAIS: "México",
  REGIMEN: "",
  RegimenClave: "",
  Estatus: true,
};

// Catálogo SAT c_RegimenFiscal (mismo usado en ClienteForm.js para régimen del receptor)
const REGIMEN_FISCAL_OPTIONS = [
  { value: "601", label: "General de Ley Personas Morales" },
  { value: "603", label: "Personas Morales con Fines no Lucrativos" },
  { value: "605", label: "Sueldos y Salarios e Ingresos Asimilados a Salarios" },
  { value: "606", label: "Arrendamiento" },
  { value: "607", label: "Régimen de Enajenación o Adquisición de Bienes" },
  { value: "608", label: "Demás ingresos" },
  { value: "610", label: "Residentes en el Extranjero sin Establecimiento Permanente en México" },
  { value: "611", label: "Ingresos por Dividendos (socios y accionistas)" },
  { value: "612", label: "Personas Físicas con Actividades Empresariales y Profesionales" },
  { value: "614", label: "Ingresos por intereses" },
  { value: "615", label: "Régimen de los ingresos por obtención de premios" },
  { value: "616", label: "Sin obligaciones fiscales" },
  { value: "620", label: "Sociedades Cooperativas de Producción que optan por diferir sus ingresos" },
  { value: "621", label: "Incorporación Fiscal" },
  { value: "622", label: "Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras" },
  { value: "623", label: "Opcional para Grupos de Sociedades" },
  { value: "624", label: "Coordinados" },
  { value: "625", label: "Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas" },
  { value: "626", label: "Régimen Simplificado de Confianza" },
];

// El backend serializa en camelCase de ASP.NET Core, pero como el modelo usa
// nombres ALL_CAPS con guion bajo (NOMBRE_EMPRESA), el resultado queda con
// mezcla rara de mayúsculas (ej. "nombrE_EMPRESA") — normalizamos todo a
// minúsculas antes de leer, igual que ya hace FacturaPrint40.jsx.
const norm = (obj) => {
  if (!obj || typeof obj !== "object") return {};
  return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k.toLowerCase(), v]));
};

const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

export default function EmpresasPage() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const { toasts, closeToast, error: toastError, success: toastSuccess } = useToast();

  const cargar = useCallback(() => {
    setLoading(true);
    obtenerEmpresas()
      .then(({ data }) => {
        const lista = Array.isArray(data) ? data : (data?.data ?? data?.Data ?? []);
        setRows(lista.map((e) => {
          const n = norm(e);
          return {
            id: n.id,
            empresa: n.empresa,
            nombre: n.nombre_empresa ?? "",
            rfc: n.rfc_empresa ?? "",
            ciudad: n.ciudad_empresa ?? "",
            estado: n.estado_empresa ?? "",
            estatus: n.estatus ?? true,
            raw: e,
          };
        }));
      })
      .catch((e) => toastError(e?.response?.data?.title || e?.message || "No se pudieron cargar las empresas"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      String(r.empresa).toLowerCase().includes(q) ||
      r.nombre.toLowerCase().includes(q) ||
      r.rfc.toLowerCase().includes(q)
    );
  }, [rows, search]);

  const openNew = () => {
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (row) => {
    const n = norm(row.raw);
    setForm({
      Id: n.id,
      EMPRESA: n.empresa ?? "",
      NOMBRE_EMPRESA: n.nombre_empresa ?? "",
      CALLE_EMPRESA: n.calle_empresa ?? "",
      COLONIA_EMPRESA: n.colonia_empresa ?? "",
      NUM_EXT_EMPRESA: n.num_ext_empresa ?? "",
      NUM_INT_EMPRESA: n.num_int_empresa ?? "",
      CP_EMPRESA: n.cp_empresa ?? "",
      TELEFONOS_EMPRESA: n.telefonos_empresa ?? "",
      CIUDAD_EMPRESA: n.ciudad_empresa ?? "",
      ESTADO_EMPRESA: n.estado_empresa ?? "",
      RFC_EMPRESA: n.rfc_empresa ?? "",
      PAIS: n.pais ?? "",
      REGIMEN: n.regimen ?? "",
      RegimenClave: n.regimenclave ?? "",
      Estatus: n.estatus ?? true,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const isEdit = !!form.Id;
      const payload = { ...form, EMPRESA: Number(form.EMPRESA) || 0 };
      if (isEdit) {
        await actualizarEmpresa(form.Id, payload);
        toastSuccess("Empresa actualizada correctamente.");
      } else {
        await crearEmpresa(payload);
        toastSuccess("Empresa creada correctamente.");
      }
      setShowModal(false);
      cargar();
    } catch (err) {
      const d = err?.response?.data;
      if (d?.errors && typeof d.errors === "object") {
        toastError(Object.entries(d.errors).map(([k, arr]) => `${k}: ${(arr ?? []).join(" ")}`).join(" | "));
      } else {
        toastError(d?.title || d?.message || err?.message || "No se pudo guardar.");
      }
    } finally {
      setSaving(false);
    }
  };

  const setField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const setBool = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.checked }));

  const columns = [
    { key: "empresa", title: "Código", style: { width: 80 } },
    { key: "nombre", title: "Nombre" },
    { key: "rfc", title: "RFC", style: { width: 130 } },
    { key: "ciudad", title: "Ciudad", style: { width: 130 } },
    { key: "estado", title: "Estado", style: { width: 130 } },
    {
      key: "estatus", title: "Estatus", style: { width: 100 },
      render: (r) => (
        <span className={`lmd-badge ${r.estatus ? "lmd-badge-success" : "lmd-badge-neutral"}`}>
          {r.estatus ? "Vigente" : "Baja"}
        </span>
      ),
    },
    {
      key: "acciones", title: "", style: { width: 100 },
      render: (r) => (
        <button className="lmd-btn lmd-btn-warning lmd-btn-sm" onClick={() => openEdit(r)}>Editar</button>
      ),
    },
  ];

  return (
    <>
      <Toast toasts={toasts} onClose={closeToast} />
      <PageHeader title="Empresas" subtitle="Mantenimiento de empresas del sistema" />

      <div className="lmd-card">
        <TableToolbar
          searchPlaceholder="Buscar por código, nombre o RFC..."
          searchValue={search}
          onSearchChange={setSearch}
          onSearch={() => {}}
          onAdd={openNew}
          addText="Nueva Empresa"
        />

        {loading && (
          <div className="lmd-loading-bar">
            <div className="lmd-spinner" /> Cargando empresas...
          </div>
        )}

        <DataTable columns={columns} rows={filtered} keyField="id" />

        <div className="lmd-pagination">
          <span className="lmd-pagination-info">
            Total: <strong>{filtered.length}</strong> empresas
          </span>
        </div>
      </div>

      {showModal && (
        <div className="lmd-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="lmd-modal lmd-modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="lmd-modal-header">
              <h3 className="lmd-modal-title">{form.Id ? "Editar Empresa" : "Nueva Empresa"}</h3>
              <button className="lmd-modal-close" onClick={() => setShowModal(false)}><CloseIcon /></button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="lmd-modal-body">
                <div className="lmd-form-grid lmd-form-grid-2" style={{ gap: 16 }}>

                  <div>
                    <label className="lmd-label">Código de empresa *</label>
                    <input
                      type="number" className="lmd-input" required
                      value={form.EMPRESA}
                      disabled={!!form.Id}
                      onChange={setField("EMPRESA")}
                      title={form.Id ? "El código no se puede cambiar una vez creada la empresa" : ""}
                    />
                  </div>

                  <div>
                    <label className="lmd-label">Nombre de la empresa *</label>
                    <input className="lmd-input" maxLength={100} required value={form.NOMBRE_EMPRESA} onChange={setField("NOMBRE_EMPRESA")} />
                  </div>

                  <div>
                    <label className="lmd-label">RFC *</label>
                    <input className="lmd-input" maxLength={13} required value={form.RFC_EMPRESA} onChange={setField("RFC_EMPRESA")} style={{ textTransform: "uppercase" }} />
                  </div>

                  <div>
                    <label className="lmd-label">Régimen fiscal *</label>
                    <input className="lmd-input" maxLength={100} required value={form.REGIMEN} onChange={setField("REGIMEN")} />
                  </div>

                  <div>
                    <label className="lmd-label">Clave de régimen *</label>
                    <select
                      className="lmd-select"
                      style={{ width: "100%" }}
                      required
                      value={form.RegimenClave}
                      onChange={(e) => {
                        const clave = e.target.value;
                        const opt = REGIMEN_FISCAL_OPTIONS.find((o) => o.value === clave);
                        setForm((f) => ({
                          ...f,
                          RegimenClave: clave,
                          REGIMEN: opt ? opt.label : f.REGIMEN,
                        }));
                      }}
                    >
                      <option value="">-- Seleccionar --</option>
                      {REGIMEN_FISCAL_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.value} - {o.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="lmd-label">Teléfonos</label>
                    <input className="lmd-input" maxLength={50} value={form.TELEFONOS_EMPRESA} onChange={setField("TELEFONOS_EMPRESA")} />
                  </div>

                  <div style={{ gridColumn: "1/-1", marginTop: 8, paddingTop: 12, borderTop: "1px solid var(--clr-border)" }}>
                    <label className="lmd-label" style={{ marginBottom: 12, display: "block" }}>Domicilio fiscal</label>
                  </div>

                  <div>
                    <label className="lmd-label">Calle *</label>
                    <input className="lmd-input" maxLength={100} required value={form.CALLE_EMPRESA} onChange={setField("CALLE_EMPRESA")} />
                  </div>

                  <div>
                    <label className="lmd-label">Colonia *</label>
                    <input className="lmd-input" maxLength={100} required value={form.COLONIA_EMPRESA} onChange={setField("COLONIA_EMPRESA")} />
                  </div>

                  <div>
                    <label className="lmd-label">Núm. exterior *</label>
                    <input className="lmd-input" maxLength={20} required value={form.NUM_EXT_EMPRESA} onChange={setField("NUM_EXT_EMPRESA")} />
                  </div>

                  <div>
                    <label className="lmd-label">Núm. interior</label>
                    <input className="lmd-input" maxLength={20} value={form.NUM_INT_EMPRESA} onChange={setField("NUM_INT_EMPRESA")} />
                  </div>

                  <div>
                    <label className="lmd-label">Código postal *</label>
                    <input className="lmd-input" maxLength={6} required value={form.CP_EMPRESA} onChange={setField("CP_EMPRESA")} />
                  </div>

                  <div>
                    <label className="lmd-label">Ciudad *</label>
                    <input className="lmd-input" maxLength={50} required value={form.CIUDAD_EMPRESA} onChange={setField("CIUDAD_EMPRESA")} />
                  </div>

                  <div>
                    <label className="lmd-label">Estado *</label>
                    <input className="lmd-input" maxLength={50} required value={form.ESTADO_EMPRESA} onChange={setField("ESTADO_EMPRESA")} />
                  </div>

                  <div>
                    <label className="lmd-label">País *</label>
                    <input className="lmd-input" maxLength={100} required value={form.PAIS} onChange={setField("PAIS")} />
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 10, paddingTop: 20 }}>
                    <input type="checkbox" id="emp-status" checked={form.Estatus} onChange={setBool("Estatus")}
                      style={{ width: 17, height: 17, cursor: "pointer", accentColor: "var(--clr-primary)" }} />
                    <label htmlFor="emp-status" className="lmd-label" style={{ marginBottom: 0, cursor: "pointer" }}>
                      Empresa vigente
                    </label>
                  </div>

                </div>
              </div>

              <div className="lmd-modal-footer">
                <button type="button" className="lmd-btn lmd-btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="lmd-btn lmd-btn-primary" disabled={saving}>
                  {saving ? "Guardando..." : "Guardar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
